/**
 * Comprehensive Logging Strategy for CoachSim
 * Structured logging with Sentry integration and local development support
 */

import React from 'react';
import { loggerLazy as sentryLogger } from './sentry-lazy';
// trackUserAction is used in userAction.click; keep it via dynamic load to avoid eager Sentry
const trackUserActionLazy = (name: string, data?: Record<string, unknown>) => {
  import('./sentry-config').then(m => m.trackUserAction(name, data || {})).catch(() => void 0);
};
// Read production mode from the leaf validator module, not './config': config imports
// this logger, and the singleton below calls this during module evaluation. Importing it
// from config made a logger <-> config cycle that crashed at load ("Cannot access
// 'isProduction' before initialization") whenever config was evaluated before logger.
// Guarded by src/lib/__tests__/import-cycle.logger-config.test.ts.
import { isProductionMode } from './client-env-validator';
import secureLogger, { redactString, redactObject } from '@/lib/secureLogger';

// Log levels
export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

// Log categories for better organization
export type LogCategory = 
  | 'auth' 
  | 'api' 
  | 'ui' 
  | 'session' 
  | 'payment' 
  | 'performance' 
  | 'security' 
  | 'user_action'
  | 'system';

// Log metadata interface
interface LogMetadata {
  [key: string]: string | number | boolean | null | undefined;
}

// Log context interface
interface LogContext {
  category?: LogCategory;
  component?: string;
  feature?: string;
  userId?: string;
  sessionId?: string;
  action?: string;
  duration?: number;
  metadata?: LogMetadata;
  [key: string]: unknown;
}

// Log entry interface
interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  category?: LogCategory;
  component?: string;
  feature?: string;
  userId?: string;
  sessionId?: string;
  action?: string;
  duration?: number;
  metadata?: LogMetadata;
}

/**
 * The Error behind an error-level log call — passed as the context itself
 * (`logger.error('CSRF self-heal failed', e)`) or as `context.error` — copied
 * with its message and stack redacted, so Sentry gets a real exception with a
 * stack instead of a bare message. Undefined when there is none.
 */
export function redactedErrorFrom(context: unknown): Error | undefined {
  const candidate = context instanceof Error
    ? context
    : (context && typeof context === 'object' && (context as { error?: unknown }).error instanceof Error
      ? (context as { error: Error }).error
      : undefined);
  if (!candidate) return undefined;
  const copy = new Error(redactString(candidate.message || String(candidate)));
  copy.name = candidate.name || 'Error';
  if (candidate.stack) copy.stack = redactString(candidate.stack);
  return copy;
}

// Enhanced logger class
class CoachSimLogger {
  private isProd = isProductionMode();
  private minLevel: LogLevel;

  constructor() {
    // Set minimum log level based on environment
    this.minLevel = this.getMinLogLevel();
  }

  private getMinLogLevel(): LogLevel {
    // Allow localStorage override for temporary debugging in production
    if (typeof window !== 'undefined') {
      const override = window.localStorage.getItem('LOG_LEVEL') as LogLevel;
      if (override && ['trace', 'debug', 'info', 'warn', 'error', 'fatal'].includes(override)) {
        return override;
      }
    }

    // Default: warn+ in production, debug+ in development
    return this.isProd ? 'warn' : 'debug';
  }

  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'];
    const currentIndex = levels.indexOf(level);
    const minIndex = levels.indexOf(this.minLevel);
    return currentIndex >= minIndex;
  }

  /**
   * Core logging method with automatic PII redaction
   *
   * SECURITY: All logs are automatically redacted before being sent to Sentry
   * or stored in localStorage. This ensures compliance with OWASP logging
   * guidelines and GDPR data minimization requirements.
   *
   * Redaction includes:
   * - API keys (OpenAI, Stripe, Resend, etc.)
   * - Authentication tokens (JWT, bearer tokens, session IDs)
   * - User PII (email, phone, address, names)
   * - Passwords and secrets
   * - Authorization headers and cookies
   *
   * @param level - Log level (trace, debug, info, warn, error, fatal)
   * @param message - Log message (will be redacted)
   * @param context - Additional context (will be redacted)
   *
   * @example
   * logger.info('User action', { userId: '123', action: 'click' }); // Safe
   * logger.error('API failed', { apiKey: 'sk-123' }); // apiKey → [REDACTED]
   */
  private log(
    level: LogLevel,
    message: string,
    context: LogContext = {}
  ) {
    // Early exit if log level below threshold (performance optimization)
    if (!this.shouldLog(level)) return;

    const timestamp = new Date().toISOString();

    // SECURITY: Redact sensitive data BEFORE any output
    // This ensures Sentry, localStorage, and console receive only safe data
    const redactedMessage = redactString(message);
    const redactedContext = redactObject(context);

    const logEntry: LogEntry = {
      timestamp,
      level,
      message: redactedMessage,
      ...redactedContext,
    };

    // Development console output (uses redacted data)
    // Only output in development for non-trace levels to avoid noise
    if (!this.isProd && level !== 'trace') {
      const prefix = this.getConsolePrefix(level, redactedContext.category);
      const contextStr = Object.keys(redactedContext).length > 0
        ? ` ${JSON.stringify(redactedContext)}`
        : '';

      // Use secureLogger for console output (belt-and-braces: already redacted, but secureLogger adds env awareness)
      const logMethod = level === 'fatal' ? 'error' : level as 'debug' | 'info' | 'warn' | 'error';
      secureLogger[logMethod](`${prefix} ${redactedMessage}${contextStr}`);
    }

    // Send to Sentry with appropriate level (REDACTED DATA)
    const normalized = redactedContext as Record<string, unknown> | undefined;

    if (level === 'trace') {
      sentryLogger.trace(redactedMessage, normalized);
    } else if (level === 'debug') {
      sentryLogger.debug(redactedMessage, normalized);
    } else if (level === 'info') {
      sentryLogger.info(redactedMessage, normalized);
    } else if (level === 'warn') {
      sentryLogger.warn(redactedMessage, normalized);
    } else if (level === 'error') {
      sentryLogger.error(redactedMessage, normalized, redactedErrorFrom(context));
    } else if (level === 'fatal') {
      sentryLogger.fatal(redactedMessage, normalized, redactedErrorFrom(context));
    }

    // Store critical errors locally for debugging (REDACTED DATA)
    if (level === 'error' || level === 'fatal') {
      this.storeCriticalLog(logEntry);
    }
  }
  
  private getConsolePrefix(level: LogLevel, category?: LogCategory): string {
    const levelEmojis: Record<LogLevel, string> = {
      trace: '🔍',
      debug: '🐛',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
      fatal: '💀',
    };
    
    const categoryEmojis: Record<LogCategory, string> = {
      auth: '🔐',
      api: '🌐',
      ui: '🎨',
      session: '💬',
      payment: '💳',
      performance: '⚡',
      security: '🛡️',
      user_action: '👆',
      system: '⚙️',
    };
    
    const levelEmoji = levelEmojis[level] || 'ℹ️';
    const categoryEmoji = category ? categoryEmojis[category] || '' : '';
    
    return `${levelEmoji} ${categoryEmoji} [${level.toUpperCase()}]`;
  }
  
  private storeCriticalLog(logEntry: LogEntry) {
    try {
      const criticalLogs = JSON.parse(localStorage.getItem('criticalLogs') || '[]') as LogEntry[];
      criticalLogs.push(logEntry);
      
      // Keep only last 50 critical logs
      if (criticalLogs.length > 50) {
        criticalLogs.splice(0, criticalLogs.length - 50);
      }
      
      localStorage.setItem('criticalLogs', JSON.stringify(criticalLogs));
    } catch (error) {
      secureLogger.error('Failed to store critical log', { context: 'logger', action: 'storeCriticalLog', error: error instanceof Error ? error.message : String(error) });
    }
  }
  
  // Public logging methods
  trace(message: string, context: LogContext = {}) {
    this.log('trace', message, context);
  }
  
  debug(message: string, context: LogContext = {}) {
    this.log('debug', message, context);
  }
  
  info(message: string, context: LogContext = {}) {
    this.log('info', message, context);
  }
  
  warn(message: string, context: LogContext = {}) {
    this.log('warn', message, context);
  }
  
  error(message: string, context: LogContext = {}) {
    this.log('error', message, context);
  }
  
  fatal(message: string, context: LogContext = {}) {
    this.log('fatal', message, context);
  }
  
  // Specialized logging methods
  
  // Authentication logging
  auth = {
    login: (userId: string, method: string = 'email') => {
      this.info('User login attempt', {
        category: 'auth',
        action: 'login',
        userId,
        metadata: { method },
      });
    },
    
    loginSuccess: (userId: string, method: string = 'email') => {
      this.info('User login successful', {
        category: 'auth',
        action: 'login_success',
        userId,
        metadata: { method },
      });
    },
    
    loginFailed: (reason: string, metadata?: LogMetadata) => {
      this.warn('User login failed', {
        category: 'auth',
        action: 'login_failed',
        metadata: { reason, ...metadata },
      });
    },
    
    logout: (userId: string) => {
      this.info('User logout', {
        category: 'auth',
        action: 'logout',
        userId,
      });
    },
    
    sessionExpired: (userId: string) => {
      this.warn('User session expired', {
        category: 'auth',
        action: 'session_expired',
        userId,
      });
    },
  };
  
  // API logging
  api = {
    request: (method: string, url: string, userId?: string) => {
      this.debug(`API request: ${method} ${url}`, {
        category: 'api',
        action: 'request',
        userId,
        metadata: { method, url },
      });
    },
    
    response: (method: string, url: string, status: number, duration: number, userId?: string) => {
      const level = status >= 400 ? 'error' : status >= 300 ? 'warn' : 'debug';
      this.log(level, `API response: ${method} ${url} - ${status}`, {
        category: 'api',
        action: 'response',
        userId,
        duration,
        metadata: { method, url, status },
      });
    },
    
    error: (method: string, url: string, error: string, userId?: string) => {
      this.error(`API error: ${method} ${url}`, {
        category: 'api',
        action: 'error',
        userId,
        metadata: { method, url, error },
      });
    },
  };
  
  // Session logging
  session = {
    start: (sessionId: string, userId: string, sessionType: string) => {
      this.info('Coaching session started', {
        category: 'session',
        action: 'session_start',
        userId,
        sessionId,
        metadata: { sessionType },
      });
    },
    
    end: (sessionId: string, userId: string, duration: number, messagesCount: number) => {
      this.info('Coaching session ended', {
        category: 'session',
        action: 'session_end',
        userId,
        sessionId,
        duration,
        metadata: { messagesCount },
      });
    },
    
    message: (sessionId: string, userId: string, messageType: 'user' | 'assistant') => {
      this.trace('Session message', {
        category: 'session',
        action: 'message',
        userId,
        sessionId,
        metadata: { messageType },
      });
    },
    
    error: (sessionId: string, userId: string, error: string) => {
      this.error('Session error', {
        category: 'session',
        action: 'session_error',
        userId,
        sessionId,
        metadata: { error },
      });
    },
  };
  
  // User action logging
  userAction = {
    click: (element: string, userId?: string, metadata?: LogMetadata) => {
      this.trace(`User clicked: ${element}`, {
        category: 'user_action',
        action: 'click',
        userId,
        metadata: { element, ...metadata },
      });
      
      // Also track in Sentry for performance monitoring
      trackUserActionLazy(`click_${element}`, { element, ...metadata });
    },
    
    navigation: (from: string, to: string, userId?: string) => {
      this.debug(`Navigation: ${from} → ${to}`, {
        category: 'user_action',
        action: 'navigation',
        userId,
        metadata: { from, to },
      });
    },
    
    formSubmit: (formName: string, userId?: string, metadata?: LogMetadata) => {
      this.info(`Form submitted: ${formName}`, {
        category: 'user_action',
        action: 'form_submit',
        userId,
        metadata: { formName, ...metadata },
      });
    },
    
    error: (action: string, error: string, userId?: string) => {
      this.error(`User action error: ${action}`, {
        category: 'user_action',
        action: 'user_error',
        userId,
        metadata: { action, error },
      });
    },
  };
  
  // Performance logging
  performance = {
    pageLoad: (pageName: string, duration: number, userId?: string) => {
      this.debug(`Page load: ${pageName}`, {
        category: 'performance',
        action: 'page_load',
        userId,
        duration,
        metadata: { pageName },
      });
    },
    
    componentRender: (componentName: string, duration: number, userId?: string) => {
      this.trace(`Component render: ${componentName}`, {
        category: 'performance',
        action: 'component_render',
        userId,
        duration,
        metadata: { componentName },
      });
    },
    
    apiCall: (endpoint: string, duration: number, userId?: string) => {
      this.debug(`API call performance: ${endpoint}`, {
        category: 'performance',
        action: 'api_performance',
        userId,
        duration,
        metadata: { endpoint },
      });
    },
    
    slowOperation: (operation: string, duration: number, threshold: number, userId?: string) => {
      this.warn(`Slow operation: ${operation} (${duration}ms > ${threshold}ms)`, {
        category: 'performance',
        action: 'slow_operation',
        userId,
        duration,
        metadata: { operation, threshold },
      });
    },
  };
  
  // Security logging
  security = {
    attemptDetected: (type: string, details: LogMetadata) => {
      this.warn(`Security attempt detected: ${type}`, {
        category: 'security',
        action: 'security_attempt',
        metadata: { type, ...details },
      });
    },
    
    rateLimitHit: (endpoint: string, userId?: string) => {
      this.warn('Rate limit exceeded', {
        category: 'security',
        action: 'rate_limit',
        userId,
        metadata: { endpoint },
      });
    },
    
    validationError: (field: string, value: string, userId?: string) => {
      this.warn('Input validation error', {
        category: 'security',
        action: 'validation_error',
        userId,
        metadata: { field, value },
      });
    },
  };
  
  // Utility methods
  getCriticalLogs(): LogEntry[] {
    try {
      return JSON.parse(localStorage.getItem('criticalLogs') || '[]') as LogEntry[];
    } catch {
      return [];
    }
  }
  
  clearCriticalLogs() {
    localStorage.removeItem('criticalLogs');
  }
  
  // Template literal helper
  fmt(template: TemplateStringsArray, ...values: unknown[]) {
    return sentryLogger.fmt(template, ...values);
  }
}

// Create and export singleton logger instance
export const logger = new CoachSimLogger();

// Export types for external use
export type { LogContext, LogMetadata, LogEntry };

// Export hooks for React components
export const useLogger = (componentName: string, feature?: string) => {
  return React.useMemo(() => ({
    trace: (message: string, metadata?: LogMetadata) =>
      logger.trace(message, { component: componentName, feature, metadata }),
    debug: (message: string, metadata?: LogMetadata) =>
      logger.debug(message, { component: componentName, feature, metadata }),
    info: (message: string, metadata?: LogMetadata) =>
      logger.info(message, { component: componentName, feature, metadata }),
    warn: (message: string, metadata?: LogMetadata) =>
      logger.warn(message, { component: componentName, feature, metadata }),
    error: (message: string, metadata?: LogMetadata) =>
      logger.error(message, { component: componentName, feature, metadata }),
    fatal: (message: string, metadata?: LogMetadata) =>
      logger.fatal(message, { component: componentName, feature, metadata }),
  }), [componentName, feature]);
};
